import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Description, LocalHospital, Person, Psychology } from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader } from '@mui/material';
import type { AdminNoteProcessingSessionDetail } from '@store/generated/admin';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { useGetNoteProcessingSessionDetailsQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

// Local type definitions for encounter and note data
interface EncounterData {
  appointment_id?: string;
  created_at?: string;
  encounter_id: string;
  encounter_type?: string;
  input_documents?: number;
  patient_id?: string;
  status?: string;
  updated_at?: string;
}

interface NoteData {
  created_at?: string;
  encounter_type?: string;
  has_feedback?: boolean;
  note_id: string;
  patient_id?: string | null;
  patient_name?: string;
  status?: string;
  template?: string;
}

interface TranscriptRow {
  transcription_session_id: string;
  transcript_count?: number;
  created_at?: string;
  actions?: string;
}

export default function NoteProcessingSessionDetailPage() {
  const { npsId } = useParams<{ npsId: string }>();

  const { data, isLoading } = useGetNoteProcessingSessionDetailsQuery(
    { sessionId: npsId || '' },
    { skip: !npsId },
  );

  // Extract the data from the comprehensive response with proper types
  const provider = data?.provider;
  const encounter = data?.encounter as EncounterData | undefined;
  const note = data?.note as NoteData | undefined;

  // Access the session data with proper type - the note_processing_session property contains AdminNoteProcessingSessionDetail
  const sessionData = data?.note_processing_session as AdminNoteProcessingSessionDetail | undefined;

  if (!npsId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid URL parameters</h2>
            <p className="text-gray-600 mt-2">Missing session ID</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading note processing session details..." />;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Note Processing Session not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Extract providerId from the response data
  const providerId = provider?.provider_id;

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Breadcrumb */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <Link to="/admin-v2" className="text-gray-500 hover:text-gray-700">
                Search
              </Link>
            </li>
            {provider && (
              <>
                <ChevronRight className="h-5 w-5 text-gray-400" />
                <li>
                  <Link
                    to={`/admin-v2/provider/${encodeURIComponent(provider.provider_id || '')}`}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {provider.name || 'Provider'}
                  </Link>
                </li>
              </>
            )}
            <ChevronRight className="h-5 w-5 text-gray-400" />
            <li>
              <span className="text-gray-900">Note Processing Session</span>
            </li>
          </ol>
        </nav>

        {/* Main Header */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
          <div className="px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Psychology className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Note Processing Session {npsId}
                  </h1>
                  <p className="text-gray-600">Processing session for note generation</p>
                  <p className="text-sm text-gray-500">
                    Created:{' '}
                    {sessionData?.created_at ? formatDateTime(sessionData.created_at) : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Badge color={getStatusBadgeColor(sessionData?.status)}>
                  {sessionData?.status ? sessionData.status.replace('_', ' ') : 'Unknown'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Session Details attached to header */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Session ID:</span>
                <span className="text-sm text-gray-900">
                  {sessionData?.note_processing_session_id || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Primary Transcript:</span>
                <span className="text-sm text-gray-900">
                  {sessionData?.transcription_session_ids?.[0]
                    ? sessionData.transcription_session_ids[0].substring(0, 8) + '...'
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Total Transcripts:</span>
                <span className="text-sm text-gray-900">
                  {sessionData?.transcription_session_ids?.length || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Created:</span>
                <span className="text-sm text-gray-900">
                  {sessionData?.created_at ? formatDateTime(sessionData.created_at) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Updated:</span>
                <span className="text-sm text-gray-900">
                  {sessionData?.updated_at ? formatDateTime(sessionData.updated_at) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Status:</span>
                <Badge color={getStatusBadgeColor(sessionData?.status)}>
                  {sessionData?.status ? sessionData.status.replace('_', ' ') : 'Unknown'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Related Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
          {/* Provider Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Person className="mr-2 h-5 w-5" />
                Provider Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-900">
                  {provider?.name || `Provider ${providerId?.substring(0, 8)}` || 'Unknown'}
                </div>
                <div className="text-sm text-gray-500">{provider?.email}</div>
                <div className="text-xs text-gray-400">ID: {providerId}</div>
                <div className="mt-3">
                  <Link to={`/admin-v2/provider/${encodeURIComponent(providerId || '')}`}>
                    <Button variant="outlined" size="small" className="w-full">
                      View Provider
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Encounter Information */}
          {encounter ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <LocalHospital className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Encounter ID:</span>
                    <span className="text-sm text-gray-900">{encounter.encounter_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Patient:</span>
                    <span className="text-sm text-gray-900">{encounter.patient_id || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Created:</span>
                    <span className="text-sm text-gray-900">
                      {encounter.created_at ? formatDateTime(encounter.created_at) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Input Documents:</span>
                    <span className="text-sm text-gray-900">{encounter.input_documents || 0}</span>
                  </div>
                  <div className="mt-4">
                    <Link to={`/admin-v2/encounter/${encounter.encounter_id}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Encounter Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <LocalHospital className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">No encounter information available.</p>
              </CardContent>
            </Card>
          )}

          {/* Generated Note Information */}
          {note ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Generated Note
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Note ID:</span>
                    <span className="text-sm text-gray-900">{note.note_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Template:</span>
                    <span className="text-sm text-gray-900">{note.template || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <Badge color={getStatusBadgeColor(note.status)}>
                      {note.status ? note.status.replace('_', ' ') : 'Unknown'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Created:</span>
                    <span className="text-sm text-gray-900">
                      {note.created_at ? formatDateTime(note.created_at) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Has Feedback:</span>
                    <span className="text-sm text-gray-900">
                      {note.has_feedback ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="mt-4">
                    <Link to={`/admin-v2/note/${note.note_id}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Note Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Generated Note
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">No note has been generated yet.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Transcripts Information */}
        {sessionData?.transcription_session_ids &&
          sessionData.transcription_session_ids.length > 0 && (
            <div className="mt-6">
              <DataTable<TranscriptRow>
                title={`Source Transcripts (${sessionData?.transcription_session_ids?.length || 0})`}
                columns={[
                  {
                    key: 'transcription_session_id',
                    label: 'Session ID',
                    render: value => (
                      <span className="text-sm font-medium text-blue-600">
                        {value ? String(value).substring(0, 8) + '...' : 'N/A'}
                      </span>
                    ),
                  },
                  {
                    key: 'transcript_count',
                    label: 'Transcript Count',
                    render: () => <span className="text-sm text-gray-900">1</span>,
                  },
                  {
                    key: 'created_at',
                    label: 'Created',
                    render: () => <span className="text-sm text-gray-500">-</span>,
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (_value, row) =>
                      row.transcription_session_id ? (
                        <Link to={`/admin-v2/transcript/${row.transcription_session_id}`}>
                          <Button variant="outlined" size="small">
                            View Details
                          </Button>
                        </Link>
                      ) : null,
                  },
                ]}
                data={
                  sessionData?.transcription_session_ids?.map((id: string) => ({
                    transcription_session_id: id,
                    transcript_count: 1,
                    created_at: '-',
                    actions: 'view',
                  })) || []
                }
                getRowUrl={row =>
                  row.transcription_session_id
                    ? `/admin-v2/transcript/${row.transcription_session_id}`
                    : ''
                }
                loading={!sessionData}
                emptyMessage="No transcripts found for this note processing session"
              />
            </div>
          )}
      </main>
    </div>
  );
}
